import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button, Card, Input } from '@/components/ui';
import { EL_KRETSEN_LOGO_URL } from '@/lib/branding';
import { ElvisTittarFram } from '@/components/features/maskot/ElvisTittarFram';
import { PrylBakgrund } from '@/components/features/inloggning/PrylBakgrund';
import type { ElvisLage } from '@/components/features/maskot/MaskotElvis';

const ENTRA_PA = import.meta.env.VITE_ENTRA_INLOGGNING === 'pa';

/** Microsofts fyrfärgade märke — krävs av deras riktlinjer för inloggningsknappar. */
function MicrosoftMarke() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

const parametrar = (del: string) => new URLSearchParams(del.replace(/^[?#]/, ''));

/** Bara en intern sökväg duger som mål — aldrig //annan.se eller en hel URL. */
function sakerSokvag(v: string | null | undefined): string | null {
  if (!v || !v.startsWith('/') || v.startsWith('//') || v.startsWith('/\\')) return null;
  return v;
}

/**
 * Vart personen ska efter inloggningen. Lösenordsflödet har målet i
 * location.state (satt av ProtectedRoute); Microsoft-flödet lämnar sidan och
 * bär därför målet i ?till= på återkomstadressen.
 */
function malEfterInloggning(state: unknown): string {
  const from = (state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  if (from?.pathname) return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
  return sakerSokvag(parametrar(window.location.search).get('till')) ?? '/';
}

/**
 * Ett nekat eller avbrutet försök kommer tillbaka som error i adressen — i
 * frågesträngen eller i hashen beroende på flöde. Microsoft-flödet återvänder
 * alltid hit, till /login, så att felet inte försvinner i en omdirigering.
 * ?till= finns bara när försöket kom från Microsoft-knappen; ett utgånget
 * e-post- eller återställningslänk-fel får därför inte Microsoft-texten.
 */
function inloggningsfelIAdressen(): string | null {
  const fraga = parametrar(window.location.search);
  const fel = fraga.get('error') ?? parametrar(window.location.hash).get('error');
  if (!fel) return null;
  if (!fraga.has('till')) return 'Inloggningen gick inte igenom. Försök igen.';
  if (fel === 'access_denied') return 'Inloggningen avbröts eller nekades av Microsoft.';
  return 'Microsoft-inloggningen gick inte igenom. Kontrollera att du loggar in med ditt El-kretsen-konto.';
}

export function LoginPage() {
  const { user, loading: authLoading, signIn, signInMicrosoft } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msLaddar, setMsLaddar] = useState(false);
  const [msFel, setMsFel] = useState<string | null>(() => (ENTRA_PA ? inloggningsfelIAdressen() : null));
  // El-kretsen loggar in med Microsoft. Lösenordsformuläret är Samifys väg in
  // och ligger hopfällt bakom en egen knapp så länge Microsoft-inloggningen är på.
  const [samifyOppen, setSamifyOppen] = useState(!ENTRA_PA);
  // ELvis lyssnar medan någon skriver i formuläret, och en stund efteråt.
  const [skriver, setSkriver] = useState(false);
  const skrivTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(skrivTimer.current), []);

  function harSkrivit() {
    setSkriver(true);
    window.clearTimeout(skrivTimer.current);
    skrivTimer.current = window.setTimeout(() => setSkriver(false), 1300);
  }

  // Ett inskick avslutar lyssnandet – annars skymde det ett fel som kom snabbt.
  function slutaLyssna() {
    window.clearTimeout(skrivTimer.current);
    setSkriver(false);
  }

  const elvisLage: ElvisLage = msLaddar || loading ? 'glad' : skriver ? 'lyssnar' : error || msFel ? 'fel' : 'vila';

  if (!authLoading && user) {
    return <Navigate to={malEfterInloggning(location.state)} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    slutaLyssna();
    setLoading(true);
    setError(null);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) setError(error);
  }

  async function handleMicrosoft() {
    slutaLyssna();
    setMsLaddar(true);
    setMsFel(null);
    const { error } = await signInMicrosoft(malEfterInloggning(location.state));
    // Lyckas anropet lämnar webbläsaren sidan, så laddningen nollställs bara vid fel.
    if (error) {
      setMsLaddar(false);
      setMsFel(error);
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden p-4 bg-ink-50">
      <PrylBakgrund />
      <div className="relative w-full max-w-[400px] motion-safe:animate-slide-up">
        {/* Logotypen står fri ovanför kortet — märket behöver ljus botten och luft */}
        <div className="flex flex-col items-center text-center mb-[88px]">
          <img src={EL_KRETSEN_LOGO_URL} alt="El-kretsen" className="h-8 mb-4" />
          <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink-900">ELvis Hub</h1>
          <p className="text-[12.5px] text-ink-500 mt-1.5 max-w-[300px] leading-relaxed">
            Logga in för att nå ELvis, Elvira, kunskapsbasen och Kretskampen.
          </p>
        </div>

        <div className="relative">
          <ElvisTittarFram lage={elvisLage} />
          <Card className="relative z-10 p-6">
            {ENTRA_PA && (
              <>
                <Button
                  type="button"
                  fullWidth
                  size="lg"
                  loading={msLaddar}
                  leftIcon={<MicrosoftMarke />}
                  onClick={handleMicrosoft}
                >
                  Logga in med Microsoft
                </Button>
                {msFel && (
                  <p role="alert" className="mt-2 text-[11.5px] font-medium text-red-600">
                    {msFel}
                  </p>
                )}
                <div className="mt-4 flex items-center gap-3 text-[11px] text-ink-500">
                  <span className="h-px flex-1 bg-ink-200" />
                  <button
                    type="button"
                    aria-expanded={samifyOppen}
                    aria-controls="samify-inloggning"
                    onClick={() => setSamifyOppen((v) => !v)}
                    className="rounded px-1 font-medium text-ink-500 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-300"
                  >
                    Samify – logga in med e-post
                  </button>
                  <span className="h-px flex-1 bg-ink-200" />
                </div>
              </>
            )}
            {samifyOppen && (
              <form id="samify-inloggning" onSubmit={handleSubmit} className={ENTRA_PA ? 'mt-4 space-y-2.5' : 'space-y-2.5'}>
                <Input
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus={ENTRA_PA}
                  placeholder="E-post"
                  value={email}
                  onChange={(e) => {
                  setEmail(e.target.value);
                  harSkrivit();
                }}
                  leftIcon={<Mail size={16} strokeWidth={2} />}
                />
                <Input
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="Lösenord"
                  value={password}
                  onChange={(e) => {
                  setPassword(e.target.value);
                  harSkrivit();
                }}
                  leftIcon={<Lock size={16} strokeWidth={2} />}
                  error={error ?? undefined}
                />
    
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  loading={loading}
                  rightIcon={<ArrowRight size={16} />}
                  className="!mt-3.5"
                >
                  Logga in
                </Button>
              </form>
            )}
          </Card>
        </div>

        <div className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-ink-500">
          <ShieldCheck size={13} strokeWidth={2} />
          <span>Krypterad anslutning · konton administreras av El-kretsen</span>
        </div>
      </div>
    </div>
  );
}
